import React, { useState, useMemo, useEffect } from "react";
import HStack from "@components/layout/HStack";
import {
  parseISOString,
  timeToMinutes,
  isSameDay,
  isOverlap,
  calculatePosition,
  addDays,
  getViewFromHour,
  generateHourLabels,
  calculateClosedHoursBlocks,
  calculateDateRangeBlocks,
  HOURS_PER_VIEW,
  TOTAL_VIEWS,
  MINUTES_PER_DAY,
  type ParsedDateTime,
  HOURS_CHANGE_VIEW,
} from "../ulti";
import { useSettingStore, useThemeStore } from "@/pages/taskpane/shared/stores";
import Button from "@/pages/taskpane/components/ui/Button";
import { ChevronLeftBlueIcon, ChevronRightBlueIcon } from "@/pages/taskpane/components/icons/IconCustom";
import { useFormatDateTime } from "@/pages/taskpane/shared/hooks";
import { useTranslation } from "react-i18next";

// Types
export interface Meeting {
  id: string;
  title: string;
  startTime: string; // ISO format with timezone
  endTime: string;
  preBufferTime?: number; // Buffer time before meeting (in minutes)
  postBufferTime?: number; // Buffer time after meeting (in minutes)
}

interface TimelineProps {
  isResourceAvailable: boolean;
  selectedStartTime: string; // ISO string - appointment start time
  selectedEndTime: string; // ISO string - appointment end time
  meetings?: Meeting[];
  currentDate?: string; // ISO string
  openStartTime: string | null; // ISO string - resource opening start time (empty if not set)
  openEndTime: string | null; // ISO string - resource opening end time (empty if not set)
  closeDates: string[]; // ISO strings
  preBufferTime: number;
  postBufferTime: number;
  openingDate: string | null;
  closingDate: string | null;
  onDateChange?: (newDate: string) => void; // Callback when display date changes
}

const Timeline = ({
  isResourceAvailable,
  selectedStartTime = "2025-11-11T07:30:00+01:00",
  selectedEndTime = "2025-11-11T09:00:00+01:00",
  meetings = [],
  currentDate = "2025-11-11",
  openStartTime = "", // Empty by default - no closed hours bars unless resource has opening hours
  openEndTime = "", // Empty by default - no closed hours bars unless resource has opening hours
  closeDates = [],
  preBufferTime = 0,
  postBufferTime = 0,
  openingDate = null,
  closingDate = null,
  onDateChange,
}: TimelineProps) => {
  const { t } = useTranslation();
  const { timeFormat } = useSettingStore();
  const { handleFormatDateWithSeparator, handleFormatTimeWithLanguage } = useFormatDateTime();
  const { isDarkMode } = useThemeStore();
  const [displayDate, setDisplayDate] = useState(() => parseISOString(currentDate));
  const [currentView, setCurrentView] = useState(() => {
    // Use selectedStartTime to determine initial view
    const selectedStartParsed = parseISOString(selectedStartTime);
    return getViewFromHour(selectedStartParsed.hours);
  });

  const selectedStartParsed = useMemo(() => parseISOString(selectedStartTime), [selectedStartTime]);
  const selectedEndParsed = useMemo(() => parseISOString(selectedEndTime), [selectedEndTime]);
  const selectedStart = useMemo(() => timeToMinutes(selectedStartParsed), [selectedStartParsed]);
  const selectedEnd = useMemo(() => timeToMinutes(selectedEndParsed), [selectedEndParsed]);
  const selectedPreBufferStart = useMemo(() => preBufferTime > 0 ? selectedStart - preBufferTime : selectedStart, [selectedStart, preBufferTime]);
  const selectedPostBufferEnd = useMemo(() => postBufferTime > 0 ? selectedEnd + postBufferTime : selectedEnd, [selectedEnd, postBufferTime]);

  // Update displayDate when currentDate prop changes
  useEffect(() => {
    const newDate = parseISOString(currentDate);
    setDisplayDate(newDate);
  }, [currentDate]);

  // Calculate time ranges for current view
  const viewTimeRange = useMemo(() => {
    const startHour = currentView * HOURS_CHANGE_VIEW;
    const endHour = startHour + HOURS_PER_VIEW;
    return { startHour, endHour };
  }, [currentView]);

  // Check if selected time or its buffer time overlaps with any meeting
  const selectedHasOverlap = useMemo(() => {
    // If resource is unavailable, always show as overlap (red)
    if (!isResourceAvailable) {
      return true;
    }

    // Calculate selected buffer time range
    return meetings.some((meeting) => {
      const meetingStartParsed = parseISOString(meeting.startTime);
      const meetingEndParsed = parseISOString(meeting.endTime);

      if (!isSameDay(meetingStartParsed, displayDate)) return false;

      const mStart = timeToMinutes(meetingStartParsed);
      const mEnd = timeToMinutes(meetingEndParsed);

      return isOverlap(selectedPreBufferStart, selectedPostBufferEnd, mStart, mEnd)
    });
  }, [selectedPreBufferStart, selectedPostBufferEnd, meetings, displayDate, isResourceAvailable]);

  // Calculate buffer time blocks (pre-buffer and post-buffer)
  const bufferBlocks = useMemo(() => {
    const blocks: Array<{
      id: string;
      position: { left: string; width: string } | null;
      type: "preBuffer" | "postBuffer";
      forType: "selected" | "meeting";
      hasOverlap: boolean;
      bufferTime: number; // Lưu buffer time để hiển thị trong title
    }> = [];

    // Check if selected buffer time overlaps with meeting
    const checkSelectedBufferOverlapsMeeting = (meetingStart: number, meetingEnd: number): boolean => {
      return isOverlap(selectedPreBufferStart, selectedPostBufferEnd, meetingStart, meetingEnd);
    };

    // Calculate pre-buffer: before selected start time
    if (preBufferTime > 0) {
      // Create a new ParsedDateTime for pre-buffer start (subtract minutes)
      const preBufferStartMinutes = selectedPreBufferStart;
      const preBufferStartHour = Math.floor(preBufferStartMinutes / 60);
      const preBufferStartMin = preBufferStartMinutes % 60;

      // Handle day rollover (if pre-buffer goes to previous day, skip it)
      if (preBufferStartMinutes >= 0) {
        const preBufferStartParsed: ParsedDateTime = {
          ...selectedStartParsed,
          hours: preBufferStartHour,
          minutes: preBufferStartMin,
        };

        const preBufferPosition = calculatePosition(
          preBufferStartParsed,
          selectedStartParsed,
          displayDate,
          viewTimeRange.startHour,
          viewTimeRange.endHour
        );

        if (preBufferPosition) {
          blocks.push({
            id: "preBuffer-selected",
            position: preBufferPosition,
            type: "preBuffer",
            forType: "selected",
            hasOverlap: selectedHasOverlap,
            bufferTime: preBufferTime,
          });
        }
      }
    }

    // Calculate post-buffer: after selected end time
    if (postBufferTime > 0) {
      // Create a new ParsedDateTime for post-buffer end (add minutes)
      const postBufferEndMinutes = selectedPostBufferEnd;
      const postBufferEndHour = Math.floor(postBufferEndMinutes / 60);
      const postBufferEndMin = postBufferEndMinutes % 60;

      // Handle day rollover (if post-buffer goes to next day, clamp to end of day)
      if (postBufferEndMinutes <= MINUTES_PER_DAY) {
        const postBufferEndParsed: ParsedDateTime = {
          ...selectedEndParsed,
          hours: postBufferEndHour,
          minutes: postBufferEndMin,
        };

        const postBufferPosition = calculatePosition(
          selectedEndParsed,
          postBufferEndParsed,
          displayDate,
          viewTimeRange.startHour,
          viewTimeRange.endHour
        );

        if (postBufferPosition) {
          blocks.push({
            id: "postBuffer-selected",
            position: postBufferPosition,
            type: "postBuffer",
            forType: "selected",
            hasOverlap: selectedHasOverlap,
            bufferTime: postBufferTime,
          });
        }
      }
    }

    // Calculate buffer for meetings - sử dụng preBufferTime và postBufferTime từ từng meeting
    meetings.forEach((meeting) => {
      const meetingStartParsed = parseISOString(meeting.startTime);
      const meetingEndParsed = parseISOString(meeting.endTime);

      if (!isSameDay(meetingStartParsed, displayDate)) return;

      const meetingStart = timeToMinutes(meetingStartParsed);
      const meetingEnd = timeToMinutes(meetingEndParsed);

      // Check if selected buffer time overlaps with meeting
      const meetingHasOverlap = checkSelectedBufferOverlapsMeeting(meetingStart, meetingEnd);

      // Lấy preBufferTime và postBufferTime từ meeting object, fallback về 0 nếu không có
      const meetingPreBufferTime = meeting.preBufferTime ?? 0;
      const meetingPostBufferTime = meeting.postBufferTime ?? 0;

      // Pre-buffer for meeting
      if (meetingPreBufferTime > 0) {
        const preBufferStartMinutes = meetingStart - meetingPreBufferTime;
        if (preBufferStartMinutes >= 0) {
          const preBufferStartParsed: ParsedDateTime = {
            ...meetingStartParsed,
            hours: Math.floor(preBufferStartMinutes / 60),
            minutes: preBufferStartMinutes % 60,
          };
          const position = calculatePosition(
            preBufferStartParsed,
            meetingStartParsed,
            displayDate,
            viewTimeRange.startHour,
            viewTimeRange.endHour
          );
          if (position) {
            blocks.push({
              id: `preBuffer-${meeting.id}`,
              position,
              type: "preBuffer",
              forType: "meeting",
              hasOverlap: meetingHasOverlap,
              bufferTime: meetingPreBufferTime,
            });
          }
        }
      }

      // Post-buffer for meeting
      if (meetingPostBufferTime > 0) {
        const postBufferEndMinutes = meetingEnd + meetingPostBufferTime;
        if (postBufferEndMinutes <= MINUTES_PER_DAY) {
          const postBufferEndParsed: ParsedDateTime = {
            ...meetingEndParsed,
            hours: Math.floor(postBufferEndMinutes / 60),
            minutes: postBufferEndMinutes % 60,
          };
          const position = calculatePosition(
            meetingEndParsed,
            postBufferEndParsed,
            displayDate,
            viewTimeRange.startHour,
            viewTimeRange.endHour
          );
          if (position) {
            blocks.push({
              id: `postBuffer-${meeting.id}`,
              position,
              type: "postBuffer",
              forType: "meeting",
              hasOverlap: meetingHasOverlap,
              bufferTime: meetingPostBufferTime,
            });
          }
        }
      }
    });

    return blocks;
  }, [
    selectedStartParsed,
    selectedEndParsed,
    meetings,
    preBufferTime,
    postBufferTime,
    displayDate,
    viewTimeRange,
    selectedHasOverlap,
    selectedPreBufferStart,
    selectedPostBufferEnd,
  ]);

  // Get all time blocks to display
  const timeBlocks = useMemo(() => {
    const blocks: Array<{
      id: string;
      position: { left: string; width: string } | null;
      type: "selected" | "meeting";
      hasOverlap: boolean;
      title?: string;
    }> = [];

    // Add other meetings FIRST (render phía dưới)
    meetings.forEach((meeting) => {
      const meetingStartParsed = parseISOString(meeting.startTime);
      const meetingEndParsed = parseISOString(meeting.endTime);
      const position = calculatePosition(
        meetingStartParsed,
        meetingEndParsed,
        displayDate,
        viewTimeRange.startHour,
        viewTimeRange.endHour
      );

      if (position) {
        const startTimeTitle = handleFormatTimeWithLanguage(meeting.startTime)
        const endTimeTitle = handleFormatTimeWithLanguage(meeting.endTime);

        blocks.push({
          id: meeting.id,
          position,
          type: "meeting",
          hasOverlap: false,
          title: `${startTimeTitle || ""} - ${endTimeTitle || ""} ${meeting?.title || ""}`,
        });
      }
    });

    // Add selected time LAST (render phía trên, đè lên meetings)
    const selectedPosition = calculatePosition(
      selectedStartParsed,
      selectedEndParsed,
      displayDate,
      viewTimeRange.startHour,
      viewTimeRange.endHour
    );
    if (selectedPosition) {
      const startTimeISO = `${selectedStartParsed.year}-${String(selectedStartParsed.month).padStart(2, "0")}-${String(selectedStartParsed.day).padStart(2, "0")}T${String(selectedStartParsed.hours).padStart(2, "0")}:${String(selectedStartParsed.minutes).padStart(2, "0")}:${String(selectedStartParsed.seconds).padStart(2, "0")}`;
      const endTimeISO = `${selectedEndParsed.year}-${String(selectedEndParsed.month).padStart(2, "0")}-${String(selectedEndParsed.day).padStart(2, "0")}T${String(selectedEndParsed.hours).padStart(2, "0")}:${String(selectedEndParsed.minutes).padStart(2, "0")}:${String(selectedEndParsed.seconds).padStart(2, "0")}`;
      const startTimeTitle = handleFormatTimeWithLanguage(startTimeISO);
      const endTimeTitle = handleFormatTimeWithLanguage(endTimeISO);
      blocks.push({
        id: "selected",
        position: selectedPosition,
        type: "selected",
        hasOverlap: selectedHasOverlap,
        title: `${startTimeTitle || ""} - ${endTimeTitle || ""}`,
      });
    }

    return blocks;
  }, [selectedStartParsed, selectedEndParsed, meetings, currentView, displayDate, selectedHasOverlap]);

  // Navigation handlers with auto day switching
  const handlePrevView = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentView > 0) {
      setCurrentView(currentView - 1);
    } else {
      // At view 0, scroll left → go to previous day, last view (20-24h)
      const newDate = addDays(displayDate, -1);
      setDisplayDate(newDate);
      setCurrentView(TOTAL_VIEWS - 1); // Go to last view (20-24h)

      // Notify parent component about date change
      if (onDateChange) {
        const isoString = `${newDate.year}-${String(newDate.month).padStart(2, "0")}-${String(newDate.day).padStart(
          2,
          "0"
        )}`;
        onDateChange(isoString);
      }
    }
  };

  const handleNextView = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentView < TOTAL_VIEWS - 1) {
      setCurrentView(currentView + 1);
    } else {
      // At last view (20-24h), scroll right → go to next day, first view (0-4h)
      const newDate = addDays(displayDate, 1);
      setDisplayDate(newDate);
      setCurrentView(0); // Go to first view (0-4h)

      // Notify parent component about date change
      if (onDateChange) {
        const isoString = `${newDate.year}-${String(newDate.month).padStart(2, "0")}-${String(newDate.day).padStart(
          2,
          "0"
        )}`;
        onDateChange(isoString);
      }
    }
  };

  // Format opening hours for tooltip
  const openingHoursTooltip = useMemo(() => {
    if (!openStartTime || !openEndTime) {
      return t("Closed hours");
    }

    try {

      const startTimeStr = handleFormatTimeWithLanguage(openStartTime);
      const endTimeStr = handleFormatTimeWithLanguage(openEndTime);

      return `${t("Closed")}: ${endTimeStr || ""} - ${startTimeStr || ""}`;
    } catch (error) {
      console.warn("Failed to format opening hours:", error);
      return t("Closed hours");
    }
  }, [openStartTime, openEndTime, handleFormatTimeWithLanguage]);

  // Calculate closed hours blocks (gray bars) if opening hours are provided
  const closedHoursBlocks = useMemo(() => {
    if (!openStartTime || !openEndTime) {
      return []; // No opening hours, no closed hours bars
    }

    try {
      const openStartParsed = parseISOString(openStartTime);
      const openEndParsed = parseISOString(openEndTime);

      // Convert opening hours to minutes since midnight
      const openStartMinutes = timeToMinutes(openStartParsed);
      const openEndMinutes = timeToMinutes(openEndParsed);

      return calculateClosedHoursBlocks(
        openStartMinutes,
        openEndMinutes,
        displayDate,
        viewTimeRange.startHour,
        viewTimeRange.endHour
      );
    } catch (error) {
      console.warn("Failed to calculate closed hours blocks:", error);
      return [];
    }
  }, [openStartTime, openEndTime, displayDate, viewTimeRange]);

  // Calculate date range blocks (gray bars) if displayDate is outside opening/closing date range
  const dateRangeBlocks = useMemo(() => {
    return calculateDateRangeBlocks(
      openingDate,
      closingDate,
      displayDate,
      viewTimeRange.startHour,
      viewTimeRange.endHour
    );
  }, [openingDate, closingDate, displayDate, viewTimeRange]);

  // Generate hour labels for current view
  const hourLabels = useMemo(() => {
    return generateHourLabels(viewTimeRange.startHour, timeFormat);
  }, [viewTimeRange, timeFormat]);

  const handleGetStyleBlock = (isSelected: boolean, hasOverlap: boolean) => {
    let stripePattern = {};
    let bgColor = "";
    let borderColor = "";
    if (isSelected) {
      if (hasOverlap) {
        stripePattern = {
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 3px, #C4314B 5px, #C4314B 6px)`,
        };
        bgColor = "bg-rubyRed40";
        borderColor = "border-rubyRed";
      } else {
        stripePattern = {
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 3px, #38C431 5px, #38C431 6px)`,
        };
        bgColor = "bg-primaryGreen40 dark:bg-primaryGreenSolid40";
        borderColor = "border-primaryGreen";
      }
    } else {
      stripePattern = {
        backgroundImage: isDarkMode
          ? `repeating-linear-gradient(45deg, transparent, transparent 3px, #479EF5 5px, #479EF5 6px)`
          : `repeating-linear-gradient(45deg, transparent, transparent 3px, #0F6CBD 5px, #0F6CBD 6px)`,
      };
      bgColor = "bg-primaryBlue40 dark:bg-brightBlue40";
      borderColor = "border-primaryBlue dark:border-brightBlue";
    }
    return {
      stripePattern,
      bgColor,
      borderColor,
    };
  };

  return (
    <div
      className="w-full"
    >
      {/* Date header with day navigation */}
      <HStack className="mb-2">
        <p className="text-[10px] text-darkerGray dark:text-darkDarkerGray font-semibold">
          {handleFormatDateWithSeparator(displayDate)}
        </p>
      </HStack>

      {/* Timeline with scroll controls */}
      <HStack className="w-full gap-[4px]">
        <Button
          leftIcon={
            <ChevronLeftBlueIcon
              isDarkMode={isDarkMode}
              size={16}
              className="bg-ultraLightGray rounded-sm hover:bg-lightGray dark:border dark:border-darkMediumGray dark:bg-darkBackgroundColor dark:hover:bg-darkMediumGray"
            />
          }
          onClick={handlePrevView}
        />

        <div className="flex-1 relative h-3 bg-white dark:bg-darkBackgroundColor border border-lightGray rounded-[2px] dark:border-darkMediumGray">
          {/* Render date range blocks first (if outside opening/closing date range) */}
          {dateRangeBlocks.map((block, index) => (
            <div
              key={`date-range-${index}`}
              className="absolute h-full border border-slateGray bg-slateGray40 dark:border-slateGrayLight dark:bg-slateGray rounded-[2px] opacity-50"
              style={{
                left: block.left,
                width: block.width,
                zIndex: 0, // Lowest z-index, behind all other blocks
              }}
              title="Outside opening/closing date range"
            />
          ))}

          {/* Render closed hours gray bars (background layer) */}
          {closedHoursBlocks.map((block, index) => (
            <div
              key={`closed-${index}`}
              className="absolute h-full border border-slateGray bg-slateGray40 dark:border-slateGrayLight dark:bg-slateGray rounded-[2px] opacity-50"
              style={{
                left: block.left,
                width: block.width,
                zIndex: 0, // Lowest z-index, behind all other blocks
              }}
              title={openingHoursTooltip}
            />
          ))}

          {/* Render buffer time blocks with diagonal stripe pattern */}
          {bufferBlocks.map((block) => {
            if (!block.position) return null;

            const { stripePattern, bgColor, borderColor } = handleGetStyleBlock(
              block.forType === "selected",
              block.hasOverlap
            );

            return (
              <div
                key={block.id}
                className={`absolute h-full rounded-[2px] border ${bgColor} ${borderColor}`}
                style={{
                  left: block.position.left,
                  width: block.position.width,
                  zIndex: block.forType === "selected" ? 10 : 5,
                  ...stripePattern,
                }}
                title={`Buffer time: ${block?.bufferTime ? `${block.bufferTime} minutes` : ""}`}
              />
            );
          })}

          {/* Render time blocks */}
          {timeBlocks.map((block) => {
            if (!block.position) return null;
            const { stripePattern, bgColor, borderColor } = handleGetStyleBlock(
              block.type === "selected",
              block.hasOverlap
            );

            return (
              <div
                key={block.id}
                className={`absolute h-full overflow-hidden ${bgColor} border ${borderColor} rounded-[2px] transition-all`}
                style={{
                  left: block.position.left,
                  width: block.position.width,
                  zIndex: block.type === "selected" ? 10 : 5,
                }}
                title={block?.title || ""}
              />
            );
          })}
        </div>

        <Button
          rightIcon={
            <ChevronRightBlueIcon
              isDarkMode={isDarkMode}
              size={16}
              className="bg-ultraLightGray rounded-sm hover:bg-lightGray dark:border dark:border-darkMediumGray dark:bg-darkBackgroundColor dark:hover:bg-darkMediumGray"
            />
          }
          onClick={handleNextView}
        />
      </HStack>

      {/* Hour labels */}
      <HStack className="w-full text-[10px] text-mediumGray dark:text-midGray mt-1">
        <div className="w-5 text-center">
          <span>{hourLabels[0]}</span>
        </div>
        <HStack justifyContent="between" className="flex-1">
          {hourLabels.map((label, index) => (
            <HStack key={index} className="w-[2px]" justifyContent="center">
              <span className={` ${index === 0 || index === hourLabels.length - 1 ? "invisible" : ""}`}>{label}</span>
            </HStack>
          ))}
        </HStack>
        <div className="w-5 text-center">
          <span>{hourLabels[hourLabels.length - 1]}</span>
        </div>
      </HStack>
    </div>
  );
};

export default Timeline;
